import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserCircle } from '@fortawesome/pro-light-svg-icons/faUserCircle';
import { faIdCard } from '@fortawesome/pro-light-svg-icons/faIdCard';
import { SerializedFile } from 'fym-common/src/db/serializersTypes';
import { ARTIST_NAME_MAX_LENGTH } from 'fym-shared/src/smartlinks/artistName';
import { TypedMemo } from '../../common/types';
import { Panel, PanelBody, PanelHead } from '../../music24/components/primitives/Panel';
import { ProfileAvatarField } from './ProfileAvatarField';
import { looksLikeSystemName } from './looksLikeSystemName';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole profile editor; each part uses its own slice of it */
import styles from './SMLProfile.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ProfileIdentityCardProps {
  accountId: number;
  name: string;
  avatar?: SerializedFile;
  onNameChange: (name: string) => void;
  onAvatarChange: (avatar: SerializedFile | undefined) => void;
  disabled?: boolean;
}

/** Who the page says you are: the picture and the name at the top of it. */
const ProfileIdentityCardComponent: React.FC<ProfileIdentityCardProps> = ({
  accountId,
  name,
  avatar,
  onNameChange,
  onAvatarChange,
  disabled,
}) => {
  const intl = useIntl();

  const onInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => onNameChange(event.target.value),
    [onNameChange]
  );

  const isSystemName = useMemo(() => looksLikeSystemName(name), [name]);

  return (
    <Panel>
      <PanelHead
        icon={<FontAwesomeIcon icon={faIdCard} />}
        title={intl.formatMessage({ id: 'app.sml.profile.identity.title', defaultMessage: 'Identity' })}
        right={
          <span className={styles.headNote}>
            {intl.formatMessage({
              id: 'app.sml.profile.identity.note',
              defaultMessage: 'shown on every page',
            })}
          </span>
        }
      />
      <PanelBody stack>
        <ProfileAvatarField accountId={accountId} avatar={avatar} onChange={onAvatarChange} disabled={disabled} />
        <div className={styles.field}>
          <label className={styles.fieldLabel} htmlFor="sml-profile-name">
            {intl.formatMessage({ id: 'app.sml.profile.name.label', defaultMessage: 'Display name' })}
          </label>
          <div className={styles.inputBox}>
            <FontAwesomeIcon icon={faUserCircle} />
            <input
              id="sml-profile-name"
              className={styles.input}
              value={name}
              // The account PATCH refuses a longer name (QA finding brk-13).
              maxLength={ARTIST_NAME_MAX_LENGTH}
              disabled={disabled}
              onChange={onInputChange}
              placeholder={intl.formatMessage({
                id: 'app.sml.profile.name.placeholder',
                defaultMessage: 'Your artist name',
              })}
            />
          </div>
          <span className={clsx(styles.fieldHelp, isSystemName && styles.warnText)}>
            {isSystemName
              ? intl.formatMessage({
                  id: 'app.sml.profile.name.system-warning',
                  defaultMessage: 'This looks like a system name. Fans will see it — use your artist name.',
                })
              : intl.formatMessage({
                  id: 'app.sml.profile.name.help',
                  defaultMessage: 'The name at the top of your bio link and on every release page.',
                })}
          </span>
        </div>
      </PanelBody>
    </Panel>
  );
};

export const ProfileIdentityCard = TypedMemo(ProfileIdentityCardComponent);
